import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Pencil } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { CANVAS, TONES, MONO } from './theme';
import type { StateValue } from './types';

/**
 * The pseudocode with the executing line highlighted. Pairing the moving canvas
 * with the exact line responsible is the mechanism that makes these animations
 * teach rather than merely illustrate.
 */
export function PseudocodePanel({
  title,
  note,
  lines,
  activeLine,
  problemSlug,
  batchId,
  topicId,
}: {
  title: string;
  note?: string;
  lines: string[];
  activeLine?: number;
  problemSlug?: string;
  batchId?: string;
  topicId?: string;
}) {
  const [copied, setCopied] = useState(false);
  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);

  // Keep the executing line in view on long listings without scrolling the page.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeLine]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
    } catch {
      /* clipboard blocked — the button simply doesn't confirm */
    }
  };

  const gutterWidth = String(lines.length).length;

  return (
    <section
      className="rounded-2xl border"
      style={{ backgroundColor: CANVAS.panel, borderColor: CANVAS.panelBorder }}
      aria-label="Pseudocode"
    >
      <header className="flex items-center justify-between gap-2 px-4 pt-3.5">
        <h3 className="truncate text-[15px] font-bold" style={{ color: CANVAS.text }}>{title}</h3>
        <div className="flex shrink-0 items-center gap-2">
          {problemSlug && batchId && topicId && (
            <Link
              to="/problems/$slug"
              params={{ slug: problemSlug }}
              search={{ batchId, topicId }}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-bold text-[#1c1917] transition-opacity hover:opacity-90"
              style={{ backgroundColor: TONES.accent }}
            >
              <Pencil className="size-3.5" />
              Practice
            </Link>
          )}
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors"
            style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textDim }}
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? 'Copied' : 'copy'}
          </button>
        </div>
      </header>

      {/* Why this approach costs what it costs — the reasoning that belongs
          next to the code rather than crammed into the complexity badge. */}
      {note && (
        <p className="px-4 pt-1.5 text-[12px] leading-relaxed" style={{ color: CANVAS.textDim }}>
          {note}
        </p>
      )}

      <div className="max-h-[420px] overflow-auto px-2 py-3">
        <pre className="m-0 text-[13px] leading-[1.9]" style={{ fontFamily: MONO }}>
          {lines.map((line, i) => {
            const n = i + 1;
            const isActive = n === activeLine;
            return (
              <div
                key={i}
                ref={isActive ? activeRef : undefined}
                className="flex items-start gap-2 rounded-md px-2"
                style={{
                  backgroundColor: isActive ? 'rgba(255, 138, 76, 0.14)' : undefined,
                  boxShadow: isActive ? `inset 0 0 0 1px ${TONES.accent}55` : undefined,
                }}
              >
                <span
                  className="shrink-0 select-none text-right"
                  style={{
                    color: isActive ? TONES.accent : CANVAS.textFaint,
                    width: `${gutterWidth + 1}ch`,
                  }}
                  aria-hidden
                >
                  {isActive ? '▸' : ' '}
                  {n}
                </span>
                {/*
                  `pre-wrap`, not `pre`: leading indentation still renders, but a
                  trailing comment wraps instead of being clipped at the panel
                  edge. Those comments carry the reasoning ("// all zeros ⟹ every
                  letter was used equally"), so losing them loses the lesson.
                */}
                <code
                  className="whitespace-pre-wrap break-words"
                  style={{ color: isActive ? CANVAS.text : CANVAS.textDim }}
                >
                  {line || ' '}
                </code>
              </div>
            );
          })}
        </pre>
      </div>
    </section>
  );
}

/** Live scalar variables for the current step. */
export function StatePanel({ state }: { state?: Record<string, StateValue> }) {
  const entries = Object.entries(state ?? {});
  if (entries.length === 0) return null;

  return (
    <section
      className="rounded-2xl border px-4 py-3.5"
      style={{ backgroundColor: CANVAS.panel, borderColor: CANVAS.panelBorder }}
      aria-label="Variable state"
    >
      <h3 className="text-[15px] font-bold" style={{ color: CANVAS.text }}>state</h3>
      <dl className="mt-2.5 space-y-1.5" style={{ fontFamily: MONO }}>
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-baseline justify-between gap-4 text-[13px]">
            <dt className="truncate" style={{ color: CANVAS.textDim }}>{key}</dt>
            <dd className="shrink-0 font-semibold" style={{ color: CANVAS.text }}>
              {value === null ? '—' : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
